import { useEffect, useRef, useState } from 'react'

import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError, ISOLATION_ALREADY_OPEN_MESSAGE } from '../../lib/api/messages.ts'
import { machines } from '../../lib/api/resources.ts'
import type { MachineIsolation } from '../../lib/api/types.ts'
import { RELEASE_TTL_DEFAULT_SECONDS } from '../../lib/api/types.ts'
import { ISOLATE_CONSEQUENCE, ISOLATE_KIND, isolateOutcome } from '../../lib/commands/isolation.ts'
import { isAwaitingAgent, isConfirmed, openCommandIdOf, releaseOutcome } from '../../lib/commands/semantics.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import { formatDayClock } from '../../lib/format.ts'
import { useAction } from '../../lib/useAction.ts'
import { Badge } from '../primitives/Badge.tsx'
import { Button } from '../primitives/Button.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { SelectField } from '../primitives/Field.tsx'
import { ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { useToast } from '../primitives/Toast.tsx'
import { ReleaseStatus } from '../commands/CommandState.tsx'
import { TTL_OPTIONS } from './ReleaseHost.tsx'

/** O mínimo para emitir: a máquina do ALERTA (`row.endpoint_id`) e um rótulo. */
export interface IsolateTarget {
  id: string
  label: string
}

/**
 * O que se sabe da rede da máquina do alerta:
 * `checking` (lendo), `isolated`, `connected`, ou `unknown` (a leitura falhou —
 * o botão continua, e um `skipped` do agente diria que já estava isolada).
 */
export type MachineNetwork = 'checking' | 'isolated' | 'connected' | 'unknown'

export function machineNetworkOf(isolation: MachineIsolation | null | undefined): MachineNetwork {
  if (isolation == null) return 'unknown'
  return isolation.is_isolated ? 'isolated' : 'connected'
}

/**
 * O convite do alerta com isolamento RECOMENDADO. Puramente apresentacional,
 * para o render check: a máquina não está isolada por causa deste alerta, e o
 * botão some quando ela já está isolada (por outro alerta ou comando).
 */
export function RecommendedIsolation({
  network,
  isolatedSince = null,
  onIsolate,
}: {
  network: MachineNetwork
  isolatedSince?: string | null
  onIsolate: () => void
}) {
  return (
    <div className="border-l-2 border-warn bg-inset px-3 py-2" role="region" aria-label="Isolamento recomendado">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="warn">isolamento recomendado</Badge>
        {network === 'isolated' && (
          <Badge tone="crit" dot>
            {isolatedSince === null ? 'isolada' : `isolada desde ${formatDayClock(isolatedSince)}`}
          </Badge>
        )}
      </div>
      {network !== 'isolated' && (
        <p className="mt-1.5 text-sm leading-snug text-fg">O agente não isolou: a máquina segue na rede.</p>
      )}
      {network === 'checking' && <p className="mt-1 text-micro text-fg-muted">Verificando a rede da máquina…</p>}
      {(network === 'connected' || network === 'unknown') && (
        <div className="mt-2">
          <Button size="sm" variant="danger" onClick={onIsolate}>
            Isolar host
          </Button>
        </div>
      )}
    </div>
  )
}

type Phase = { kind: 'form' } | { kind: 'tracking'; id: string; initial: AgentCommand | null }

/**
 * Isolamento de rede por operador (`isolate-host` → `isolate_host`). Mesmo
 * desenho do desbloqueio (ReleaseHost.tsx): PEDIDO com justificativa e
 * confirmação, depois ACOMPANHAMENTO. "Isolada" só depois de `executed`/`skipped`,
 * e então a máquina é relida.
 */
export function IsolateHostDialog({
  machine,
  onClose,
  onIsolated,
}: {
  machine: IsolateTarget
  onClose: () => void
  /** O agente confirmou. Quem chama relê o `isolation` da máquina. */
  onIsolated: () => void
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'form' })
  const [round, setRound] = useState(0)

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Isolar host"
      description={<span className="font-medium text-fg">{machine.label}</span>}
      wide
    >
      {phase.kind === 'form' ? (
        <IsolateForm
          key={round}
          machine={machine}
          onAccepted={(command) => setPhase({ kind: 'tracking', id: command.id, initial: command })}
          onAlreadyOpen={(id) => setPhase({ kind: 'tracking', id, initial: null })}
          onCancel={onClose}
        />
      ) : (
        <IsolateTracking
          key={phase.id}
          machine={machine}
          commandId={phase.id}
          initial={phase.initial}
          onIsolated={onIsolated}
          onReissue={() => {
            setRound((n) => n + 1)
            setPhase({ kind: 'form' })
          }}
          onClose={onClose}
        />
      )}
    </Dialog>
  )
}

function IsolateForm({
  machine,
  onAccepted,
  onAlreadyOpen,
  onCancel,
}: {
  machine: IsolateTarget
  onAccepted: (command: AgentCommand) => void
  onAlreadyOpen: (id: string) => void
  onCancel: () => void
}) {
  const action = useAction()
  const [reason, setReason] = useState('')
  const [ttl, setTtl] = useState(String(RELEASE_TTL_DEFAULT_SECONDS))
  const [confirmed, setConfirmed] = useState(false)
  // Uma chave por CLIQUE em "Isolar host" (este formulário), estável entre retries.
  const idempotencyKey = useRef(crypto.randomUUID()).current

  const disabled = reason.trim() === '' || !confirmed
  const alreadyOpen = action.error instanceof AthenaError && action.error.code === 'agent_command_already_open'

  const submit = (): void => {
    if (disabled) return
    action.run(async () => {
      try {
        const command = await machines.isolateHost(machine.id, {
          reason: reason.trim(),
          idempotencyKey,
          expiresInSeconds: Number(ttl),
        })
        onAccepted(command)
      } catch (cause) {
        // Já há um isolamento OU um desbloqueio aberto (dividem a chave): leve
        // o operador até ele.
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            onAlreadyOpen(id)
            return
          }
        }
        throw cause
      }
    })
  }

  return (
    <div className="space-y-3">
      <Notice tone="crit">{ISOLATE_CONSEQUENCE}</Notice>

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">
          Justificativa <span className="text-fg-muted">*</span>
        </span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={512}
          rows={3}
          required
          autoFocus
          className="w-full border border-line-strong bg-inset px-2 py-1 text-base text-fg placeholder:text-fg-muted"
          placeholder="ex.: credenciais despejadas pelo lsass, contenção até a análise — ticket INC-4471"
        />
      </label>

      <SelectField
        label="Validade do pedido"
        value={ttl}
        onValueChange={setTtl}
        options={TTL_OPTIONS}
        className="max-w-60"
      />

      <label className="flex items-start gap-2 text-sm text-fg-secondary">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          className="mt-0.5 size-3.5 shrink-0"
          style={{ accentColor: 'var(--c-accent)' }}
        />
        <span>
          Confirmo que <span className="font-medium text-fg">{machine.label}</span> pode perder a rede.
        </span>
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        {action.error !== null && (
          <p role="alert" className="mr-auto text-sm leading-snug text-crit">
            {alreadyOpen ? ISOLATION_ALREADY_OPEN_MESSAGE : describeError(action.error)}
          </p>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="danger" busy={action.busy} disabled={disabled} onClick={submit}>
          Isolar host
        </Button>
      </div>
    </div>
  )
}

function IsolateTracking({
  machine,
  commandId,
  initial,
  onIsolated,
  onReissue,
  onClose,
}: {
  machine: IsolateTarget
  commandId: string
  initial: AgentCommand | null
  onIsolated: () => void
  onReissue: () => void
  onClose: () => void
}) {
  const toast = useToast()
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  const [verified, setVerified] = useState<MachineIsolation | null>(null)
  const announced = useRef(false)
  // Só anuncia se a confirmação aconteceu DIANTE do operador.
  const sawAwaiting = useRef(false)

  const state = command?.state ?? null
  if (state !== null && isAwaitingAgent(state)) sawAwaiting.current = true
  // O 409 pode ter levado a um DESBLOQUEIO aberto: a leitura é a dele.
  const isIsolate = command === null || command.kind === ISOLATE_KIND
  const confirmed = state !== null && isConfirmed(state) && isIsolate

  useEffect(() => {
    if (!confirmed) return
    const controller = new AbortController()
    machines
      .get(machine.id, controller.signal)
      .then((fresh) => {
        setVerified(fresh.isolation ?? null)
        if (!announced.current && sawAwaiting.current) {
          announced.current = true
          toast.success(`${machine.label} foi isolada.`)
        }
        onIsolated()
      })
      .catch(() => {
        // A releitura é cortesia: o comando já disse `executed`.
      })
    return () => controller.abort()
    // Só a confirmação dispara a releitura.
  }, [confirmed, machine.id])

  const canReissue = isIsolate && (state === 'expired' || state === 'failed' || state === 'cancelled')

  return (
    <div className="space-y-3">
      {command === null ? (
        error !== null ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <LoadingRow label="Lendo o comando…" />
        )
      ) : (
        <>
          {!isIsolate && (
            <Notice tone="neutral">
              Já há um <strong>desbloqueio</strong> aberto para esta máquina.
            </Notice>
          )}
          <ReleaseStatus command={command} outcome={isIsolate ? isolateOutcome(command.state) : releaseOutcome(command.state)} />
          {error !== null && (
            <Notice tone="warn">
              Não foi possível atualizar o estado: {describeError(error)}{' '}
              <button type="button" className="underline" onClick={refresh}>
                Tentar de novo
              </button>
            </Notice>
          )}
          {confirmed && verified !== null && !verified.is_isolated && (
            <Notice tone="warn">
              O agente confirmou, mas a máquina ainda não consta como isolada. Confira o histórico de comandos da máquina.
            </Notice>
          )}
        </>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        <span className="mr-auto font-mono text-micro text-fg-muted">comando {commandId}</span>
        {canReissue && (
          <Button variant="danger" onClick={onReissue}>
            Tentar de novo
          </Button>
        )}
        <Button variant={confirmed ? 'primary' : 'ghost'} onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  )
}
